import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { API } from "@/lib/publicSettings";
import { SERVICE_OPTIONS, BUDGET_OPTIONS, TIME_OPTIONS, PACKAGE_OPTIONS } from "@/data/site";

const EMPTY = { name: "", company: "", city: "", email: "", phone: "", website: "", service: "", package: "", budget: "", message: "", preferred_time: "", privacy_accepted: false, marketing_consent: false, hp_field: "" };

const Field = ({ id, label, required, children }) => (
  <div>
    <label htmlFor={id} className="label">{label}{required && <span className="req text-blue-700"> *</span>}</label>
    {children}
  </div>
);

const Select = ({ id, value, onChange, options, placeholder }) => (
  <select id={id} data-testid={`lead-form-${id}`} value={value} onChange={onChange} className="field">
    <option value="">{placeholder}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export default function LeadForm({ formType = "preventivo", full = false, defaultService = "", dark = false }) {
  const [params] = useSearchParams();
  const [f, setF] = useState({ ...EMPTY, service: defaultService });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  useEffect(() => {
    const pk = params.get("pacchetto");
    if (pk && PACKAGE_OPTIONS[pk]) setF((p) => ({ ...p, package: pk, service: PACKAGE_OPTIONS[pk] }));
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.privacy_accepted) { setError("Per inviare la richiesta è necessario accettare l’informativa privacy."); return; }
    setState("loading"); setError("");
    const pkg = Object.keys(PACKAGE_OPTIONS).find((k) => PACKAGE_OPTIONS[k] === f.service) || "";
    try {
      await axios.post(`${API}/public/leads`, { ...f, package: pkg, form_type: formType, page: window.location.pathname });
      setState("done");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Controlla i campi obbligatori e riprova.");
      setState("idle");
    }
  };

  if (state === "done") {
    return (
      <div data-testid="lead-form-success" className={`anim-in rounded-2xl p-8 ${dark ? "bg-white/5" : "bg-blue-50"}`}>
        <CheckCircle2 className="h-8 w-8 text-blue-700" />
        <p className={`mt-4 font-display text-xl font-bold ${dark ? "!text-white" : ""}`}>Richiesta ricevuta, grazie.</p>
        <p className={`mt-2 text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}>Ti ricontattiamo entro un giorno lavorativo all’indirizzo indicato.</p>
      </div>
    );
  }

  const muted = dark ? "text-slate-600" : "text-slate-600";
  return (
    <form onSubmit={submit} data-testid={`lead-form-${formType}`} className={`grid gap-4 ${dark ? "[&_.label]:text-slate-200 [&_.req]:text-cyan-300 [&_.field]:border-white/10 [&_.field]:bg-white/5 [&_.field]:text-white [&_.field]:placeholder:text-slate-600 [&_option]:text-slate-900" : ""}`}>
      <input type="text" name="company_website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={f.hp_field} onChange={set("hp_field")} className="hidden" />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="name" label="Nome" required><input id="name" data-testid="lead-form-name" required minLength={2} value={f.name} onChange={set("name")} className="field" autoComplete="name" /></Field>
        <Field id="email" label="Email" required><input id="email" type="email" data-testid="lead-form-email" required value={f.email} onChange={set("email")} className="field" autoComplete="email" /></Field>
        <Field id="company" label="Nome attività"><input id="company" data-testid="lead-form-company" value={f.company} onChange={set("company")} className="field" autoComplete="organization" placeholder="Facoltativo" /></Field>
        <Field id="service" label="Servizio richiesto"><Select id="service" value={f.service} onChange={set("service")} options={SERVICE_OPTIONS} placeholder="Seleziona" /></Field>
      </div>
      <Field id="phone" label="Telefono"><input id="phone" type="tel" data-testid="lead-form-phone" value={f.phone} onChange={set("phone")} className="field" autoComplete="tel" placeholder="Facoltativo" /></Field>
      {full && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="city" label="Città"><input id="city" data-testid="lead-form-city" value={f.city} onChange={set("city")} className="field" placeholder="Facoltativo" /></Field>
          <Field id="website" label="Sito attuale"><input id="website" data-testid="lead-form-website" value={f.website} onChange={set("website")} className="field" placeholder="Se esiste" /></Field>
          <Field id="budget" label="Budget"><Select id="budget" value={f.budget} onChange={set("budget")} options={BUDGET_OPTIONS} placeholder="Facoltativo" /></Field>
          <Field id="preferred_time" label="Fascia oraria preferita"><Select id="preferred_time" value={f.preferred_time} onChange={set("preferred_time")} options={TIME_OPTIONS} placeholder="Facoltativo" /></Field>
        </div>
      )}
      <Field id="message" label="Messaggio">
        <textarea id="message" data-testid="lead-form-message" rows={3} value={f.message} onChange={set("message")} className="field resize-none" placeholder="Di cosa ti occupi e di cosa hai bisogno?" />
      </Field>
      <div className={`space-y-2.5 text-sm ${muted}`}>
        <label className="flex items-start gap-3">
          <input type="checkbox" data-testid="lead-form-privacy" checked={f.privacy_accepted} onChange={set("privacy_accepted")} className="mt-0.5 h-4 w-4 shrink-0 accent-blue-600" />
          <span>Ho letto l’<Link to="/privacy-policy" className={`${dark ? "text-cyan-300" : "text-blue-700"} underline underline-offset-2`}>informativa privacy</Link>. I dati servono solo a rispondere a questa richiesta. <span className="text-blue-700">*</span></span>
        </label>
        <label className="flex items-start gap-3">
          <input type="checkbox" data-testid="lead-form-marketing" checked={f.marketing_consent} onChange={set("marketing_consent")} className="mt-0.5 h-4 w-4 shrink-0 accent-blue-600" />
          <span>Facoltativo: accetto di ricevere occasionali comunicazioni commerciali da STUDIO WEB NS. Revocabile in ogni momento.</span>
        </label>
      </div>
      {error && <p role="alert" data-testid="lead-form-error" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
      <button type="submit" disabled={state === "loading"} data-testid="lead-form-submit" className="btn-primary w-full !py-3.5 sm:w-auto sm:justify-self-start">
        {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Invia richiesta <ArrowRight className="h-4 w-4" /></>}
      </button>
    </form>
  );
}
